// Fully Updated AddTradePage.jsx with Tag-based Insight Suggestions and Default Date/Time

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Select, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, TextField, Typography, Autocomplete, MenuItem, Link, Divider, Chip, Paper
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useTradeDefaults } from '../hooks/useTradeDefaults';

const StatusEditInputCell = ({ id, field, value, api }) => {
  const handleChange = async (event) => {
    const newValue = event.target.value;
    await api.setEditCellValue({ id, field, value: newValue }, event);
  };
  return (
    <Select value={value || ''} onChange={handleChange} fullWidth size="small" sx={{ height: 40 }}>
      <MenuItem value="OPEN">OPEN</MenuItem>
      <MenuItem value="WIN">WIN</MenuItem>
      <MenuItem value="LOSS">LOSS</MenuItem>
      <MenuItem value="BREAKEVEN">BREAKEVEN</MenuItem>
    </Select>
  );
};

const AddTradePage = ({ initialData = null, onClose, onRefresh, filteredTrades }) => {
  const isEditMode = !!initialData;
  const { getDefaultTradeValues, settings } = useTradeDefaults();
  const defaults = getDefaultTradeValues();

  const [trades, setTrades] = useState([]);
  const [insightSuggestions, setInsightSuggestions] = useState([]);
  const [open, setOpen] = useState(isEditMode);
  const [editingTrade, setEditingTrade] = useState(initialData);
  const [strategies, setStrategies] = useState([]);
  const [strategyMap, setStrategyMap] = useState({});
  const [form, setForm] = useState({
    strategyIds: initialData?.strategyIds || [],
    symbol: initialData?.symbol || '',
    entryPrice: initialData?.entryPrice || '',
    exitPrice: initialData?.exitPrice || '',
    tradingUrl: initialData?.tradingUrl || '',
    positionType: initialData?.positionType || '',
    tradeDate: initialData?.tradeDate || defaults.tradeDate,
    tradeTime: initialData?.tradeTime || defaults.tradeTime,
    notes: initialData?.notes || '',
    tags: initialData?.tags || (defaults.tags.length > 0 ? defaults.tags.join(',') : ''),
    status: initialData?.status || defaults.status,
    profitLoss: initialData?.profitLoss || '',
    insight: initialData?.insight || '',
    positionSize: initialData?.positionSize || defaults.positionSize
  });

  useEffect(() => {
    fetchStrategies();
    if (!filteredTrades) {
      fetchTrades();
    }
  }, [filteredTrades]);

  const fetchTrades = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/getTrades', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedTrades = response.data;
      setTrades(fetchedTrades);

      const uniqueInsights = new Set();
      fetchedTrades.forEach((t) => {
        if (t.insight) {
          t.insight.split(',').map((i) => i.trim()).forEach((i) => uniqueInsights.add(i));
        }
      });
      setInsightSuggestions(Array.from(uniqueInsights));
    } catch (error) {
      console.error('Error fetching trades:', error);
    }
  };

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrategies(response.data);
      const map = {};
      response.data.forEach((s) => { map[s.strategyId] = s.name; });
      setStrategyMap(map);
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const payload = { ...form };
      if (editingTrade?.tradeId) payload.tradeId = editingTrade.tradeId;
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onClose) onClose();
      setOpen(false);
      fetchTrades?.();
      if (typeof onRefresh === 'function') onRefresh();
    } catch (error) {
      console.error('Error saving trade:', error);
    }
  };

  const handleEdit = (trade) => {
    setEditingTrade(trade);
    setForm({ ...trade, strategyIds: trade.strategyIds || [] });
    setOpen(true);
  };

  const handleDelete = async (tradeId) => {
    try {
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/deleteTrade', { tradeId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTrades();
    } catch (error) {
      console.error('Error deleting trade:', error);
    }
  };

  const handleRowUpdate = async (updatedRow, oldRow) => {
    try {
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', updatedRow, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTrades();
      return updatedRow;
    } catch (error) {
      console.error('Error updating row:', error);
      return oldRow;
    }
  };

  const columns = [
    {
      field: 'strategyIds', headerName: 'Strategies', flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {(params.row.strategyIds || []).map((id) => (
            <Box key={id} sx={{ backgroundColor: '#1976d2', color: 'white', px: 1, borderRadius: '8px', fontSize: '0.75rem' }}>{strategyMap[id] || id}</Box>
          ))}
        </Box>
      )
    },
    { field: 'symbol', headerName: 'Symbol', flex: 1, editable: true },
    { field: 'entryPrice', headerName: 'Entry Price', flex: 1, editable: true },
    { field: 'exitPrice', headerName: 'Exit Price', flex: 1, editable: true },
    { field: 'profitLoss', headerName: 'P/L', flex: 1, editable: true, renderCell: (params) => <Box sx={{ color: params.value >= 0 ? 'green' : 'red' }}>{params.value}</Box> },
    { field: 'insight', headerName: 'Insight', flex: 2, editable: true },
    {
      field: 'tradingUrl', headerName: 'View URL', flex: 1,
      renderCell: (params) => params.value ? (<Link href={params.value} target="_blank" rel="noopener"><VisibilityIcon /></Link>) : null
    },
    { field: 'positionType', headerName: 'Type', flex: 1, editable: true },
    { field: 'tradeDate', headerName: 'Date', flex: 1, editable: true },
    { field: 'tradeTime', headerName: 'Time', flex: 1, editable: true },
    { field: 'tags', headerName: 'Tags', flex: 1, editable: true },
    { field: 'notes', headerName: 'Notes', flex: 2, editable: true },
    {
      field: 'status', headerName: 'Status', flex: 1, editable: true,
      renderEditCell: (params) => <StatusEditInputCell {...params} />,
      renderCell: (params) => {
        const colorMap = { WIN: '#4caf50', LOSS: '#f44336', BREAKEVEN: '#ff9800', OPEN: '#2196f3' };
        return <Box sx={{ backgroundColor: colorMap[params.value] || '#9e9e9e', color: '#fff', px: 1, borderRadius: '8px', fontSize: '0.75rem', textAlign: 'center' }}>{params.value || 'N/A'}</Box>;
      }
    },
    {
      field: 'actions', headerName: 'Actions', width: 100,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)}><EditIcon /></IconButton>
          <IconButton color="error" onClick={() => handleDelete(params.row.tradeId)}><DeleteIcon /></IconButton>
        </>
      )
    }
  ];

  const TradeForm = (
    <Dialog open={open || isEditMode} onClose={() => { if (!isEditMode) setOpen(false); else if (onClose) onClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle>{editingTrade ? 'Edit Trade' : 'Add Trade'}</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto', px: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" gutterBottom>Trade Details</Typography>
        <Autocomplete multiple options={strategies} getOptionLabel={(option) => option.name} value={strategies.filter((s) => form.strategyIds.includes(s.strategyId))} onChange={(e, newVal) => setForm((prev) => ({ ...prev, strategyIds: newVal.map((s) => s.strategyId) }))} renderInput={(params) => <TextField {...params} label="Strategies" margin="dense" fullWidth />} />
        <TextField margin="dense" label="Symbol" name="symbol" value={form.symbol} onChange={handleChange} fullWidth />
        <TextField margin="dense" label="Entry Price" name="entryPrice" type="number" value={form.entryPrice} onChange={handleChange} fullWidth />
        <TextField margin="dense" label="Exit Price" name="exitPrice" type="number" value={form.exitPrice} onChange={handleChange} fullWidth />
        <TextField margin="dense" label={`Position Size (${settings.defaultCurrency})`} name="positionSize" type="number" value={form.positionSize} onChange={handleChange} fullWidth />
        <TextField margin="dense" label="Profit / Loss" name="profitLoss" type="number" value={form.profitLoss} onChange={handleChange} fullWidth />
        <Autocomplete
          freeSolo
          multiple
          options={insightSuggestions}
          value={form.insight ? form.insight.split(',') : []}
          onChange={(e, newVal) => setForm((prev) => ({ ...prev, insight: newVal.join(',') }))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => <Chip variant="outlined" label={option} {...getTagProps({ index })} />)
          }
          renderInput={(params) => <TextField {...params} label="Trading Insight" margin="dense" fullWidth />}
        />
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Meta</Typography>
        <TextField margin="dense" label="Tradingview URL" name="tradingUrl" value={form.tradingUrl} onChange={handleChange} fullWidth />
        <TextField select margin="dense" label="Position Type" name="positionType" value={form.positionType} onChange={handleChange} fullWidth>
          <MenuItem value="Long">Long</MenuItem>
          <MenuItem value="Short">Short</MenuItem>
        </TextField>
        <TextField margin="dense" label="Trade Date" name="tradeDate" type="date" value={form.tradeDate} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
        <TextField margin="dense" label="Trade Time" name="tradeTime" type="time" value={form.tradeTime} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
        <TextField margin="dense" label="Tags (comma-separated)" name="tags" value={form.tags} onChange={handleChange} fullWidth />
        <TextField margin="dense" label="Notes" name="notes" value={form.notes} onChange={handleChange} fullWidth multiline />
        <TextField select margin="dense" label="Status" name="status" value={form.status} onChange={handleChange} fullWidth>
          <MenuItem value="WIN">WIN</MenuItem>
          <MenuItem value="LOSS">LOSS</MenuItem>
          <MenuItem value="BREAKEVEN">BREAKEVEN</MenuItem>
          <MenuItem value="OPEN">OPEN</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions sx={{ justifyContent: { xs: 'center', sm: 'flex-end' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Button 
          onClick={() => { if (!isEditMode) setOpen(false); else if (onClose) onClose(); }}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (isEditMode) return TradeForm;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {!filteredTrades && <Typography variant="h4" gutterBottom sx={{ mb: 3, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>Trades List</Typography>}
      
      {/* Header Section */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">All Trades</Typography>
          <Button 
            variant="contained" 
            onClick={() => setOpen(true)} 
            startIcon={<AddCircleOutlineIcon />}
          >
            Add Trade
          </Button>
        </Box>
        
        <DataGrid
          rows={filteredTrades || trades}
          columns={columns}
          autoHeight
          getRowId={(row) => row.tradeId}
          experimentalFeatures={{ newEditingApi: true }}
          processRowUpdate={handleRowUpdate}
          onRowDoubleClick={(params) => handleEdit(params.row)}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 25, 50]}
          sx={{ 
            '& .MuiDataGrid-cell': { fontSize: '0.875rem' },
            '& .MuiDataGrid-columnHeaders': { backgroundColor: theme => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5' },
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            '& .MuiDataGrid-row:hover': { backgroundColor: theme => theme.palette.action.hover },
            height: 650
          }}
        />
      </Paper>
      {TradeForm}
    </Box>
  );
};

export default AddTradePage;